import { useState, useCallback, useMemo } from "react";
import { Button, Dropdown } from "antd";
import { LockFilled } from "@ant-design/icons";
import { ExitIcon, MenuIcon, StarIcon, FilledStar } from "../../assets/icons";
import MuteIcon from "../../assets/icons/Mute";
import ApiService from "../../API/apiService";
import { API_ENDPOINTS } from "../../API/endpoints";
import { handleApiError, localStorageUtil } from "../../utils/CommonFunctions";
import ConfirmationModal from "./modals/ConfirmationModal";
import { useBridgeStore } from "../../store/apis/useBridgeStore";
import { getAvatarColor, getInitials } from "../../Components/CustomAvatar";
import { formatListTime, getLatestMessagePreview } from "./listItemHelpers";

const ChannelListItem = ({
  channel,
  getChannelListApi,
  getFavListApi,
  isFavoriteList = false,
  hideMoreOptions = false,
  showNotificationType = false,
  userSelfData,
  onListUpdate,
  onClick,
}) => {
  const [isConfirmationPopup, setIsConfirmationPopup] = useState(false);
  const [isFavorite, setIsFavorite] = useState(channel?.isFavorite || false);

  // Memoized values
  const channelId = channel?._id;
  const hasUnread = useMemo(
    () =>
      channel?.unreadCount > 0 ||
      channel?.unseenMessagesCount > 0 ||
      channel?.unreadThreadCount > 0,
    [channel?.unreadCount, channel?.unseenMessagesCount, channel?.unreadThreadCount]
  );
  // Thread replies are included so a chat with only unread replies still shows a badge
  const unreadCount =
    (channel?.unreadCount || channel?.unseenMessagesCount || 0) +
      (channel?.unreadThreadCount || 0) || "";
  const canMarkAsRead = channel?.unreadCount > 0 || channel?.unreadThreadCount > 0;
  const notificationType = showNotificationType
    ? channel?.notificationType
    : null;

  // Update user data in localStorage
  const updateUserData = useCallback(
    (newMuteChannels) => {
      let userDataFromStorage = localStorageUtil.getItem("userData");
      userDataFromStorage.mute_channels = newMuteChannels;
      localStorageUtil.setItem("userData", userDataFromStorage);
    },
    [userSelfData]
  );

  const triggerSidebarRefresh = useBridgeStore(
    (store) => store.triggerSidebarRefresh
  );
  const triggerThreadsRead = useBridgeStore((store) => store.triggerThreadsRead);

  // Refresh lists after operations
  const refreshLists = useCallback(async () => {
    await Promise.all([getChannelListApi(), getFavListApi()]);
  }, [getChannelListApi, getFavListApi]);

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(async () => {
    try {
      const endpoint = isFavorite
        ? `${API_ENDPOINTS.unChannelFavApi}/${channelId}`
        : `${API_ENDPOINTS.favouriteChannelApi}/${channelId}`;

      const response = await ApiService.put(endpoint);

      if (response?.data && response?.status === 1) {
        setIsFavorite(!isFavorite);
        await refreshLists();
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [channelId, isFavorite, refreshLists]);

  // Handle mute/unmute operations
  const handleMuteToggle = useCallback(async () => {
    try {
      onListUpdate(channel._id, { isMutedByMe: !channel.isMutedByMe });
      const endpoint = channel.isMutedByMe
        ? API_ENDPOINTS.unMuteChannel
        : API_ENDPOINTS.muteChannel;
      const payload = channel.isMutedByMe
        ? { channelIdToUnmute: channelId }
        : { channelIdToMute: channelId };

      const response = await ApiService.post(endpoint, payload);

      if (response?.status === 1) {
        updateUserData(response.data.mute_channels);
      }
    } catch (error) {
      onListUpdate(channel._id, { isMutedByMe: channel.isMutedByMe });
      handleApiError(error);
    }
  }, [channelId, updateUserData, channel.isMutedByMe]);

  // Handle read/unread toggle
  const handleReadStatusToggle = useCallback(async () => {
    try {
      const isUnread = canMarkAsRead;
      const endpoint = isUnread
        ? `${API_ENDPOINTS.seenChannelMessage}/${channelId}?all=true`
        : `${API_ENDPOINTS.unreadChannelMessageApi}/${channelId}`;
      const method = isUnread ? "get" : "put";

      const response = await ApiService[method](endpoint);

      if (response?.data) {
        let userDataFromStorage = localStorageUtil.getItem("userData");
        userDataFromStorage.lastActiveChat = { id: channelId, type: "channel" };
        localStorageUtil.setItem("userData", userDataFromStorage);

        if (isFavoriteList) {
          await getFavListApi();
        } else {
          await getChannelListApi();
        }
        // Marking as read also clears thread replies, so the Threads badge must re-fetch
        if (isUnread) {
          triggerSidebarRefresh(Date.now(), "threads");
          triggerThreadsRead(channelId);
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [
    triggerSidebarRefresh,
    triggerThreadsRead,
    canMarkAsRead,
    channelId,
    userSelfData,
    isFavoriteList,
    getFavListApi,
    getChannelListApi,
  ]);

  // Handle confirmation success
  const handleConfirmationSuccess = useCallback(() => {
    refreshLists();
  }, [refreshLists]);

  // Handle confirmation modal actions
  const openConfirmationModal = useCallback(() => {
    setIsConfirmationPopup(true);
  }, []);

  const closeConfirmationModal = useCallback(() => {
    setIsConfirmationPopup(false);
  }, []);

  // Memoized menu items
  const menuItems = useMemo(
    () => [
      {
        key: "read-status",
        label: canMarkAsRead ? "Mark as read" : "Mark as unread",
        icon: <MenuIcon />,
        onClick: handleReadStatusToggle,
      },
      {
        key: "favorite",
        label: isFavorite ? "Unfavorite" : "Favorite",
        icon: isFavorite ? <FilledStar /> : <StarIcon />,
        onClick: handleFavoriteToggle,
      },
      {
        key: "mute",
        label: channel.isMutedByMe ? "Unmute Channel" : "Mute Channel",
        icon: <MuteIcon />,
        onClick: handleMuteToggle,
      },
      ...(!channel?.isDefault
        ? [
          {
            key: "leave",
            label: "Leave Channel",
            className: "text-danger",
            icon: <ExitIcon />,
            onClick: openConfirmationModal,
          },
        ]
        : []),
    ],
    [
      canMarkAsRead,
      channel?.isDefault,
      isFavorite,
      channel.isMutedByMe,
      handleReadStatusToggle,
      handleFavoriteToggle,
      handleMuteToggle,
      openConfirmationModal,
    ]
  );

  // Second line: the latest message with its sender, else the channel type
  const subtitle = useMemo(() => {
    const preview = getLatestMessagePreview(channel);
    if (!preview) return channel?.isPrivate ? "Private channel" : "Public channel";
    if (channel?.latestMessageSenderId === userSelfData?._id) return `You: ${preview}`;
    const senderName = channel?.latestMessageSenderName?.split(" ")[0];
    return senderName ? `${senderName}: ${preview}` : preview;
  }, [channel, userSelfData?._id]);

  const channelName = channel?.name || "Channels";
  const accentColor = getAvatarColor(channelName);

  return (
    <div
      className="sidebar-channel-row"
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!onClick) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      style={{ cursor: onClick ? "pointer" : "default", width: "100%" }}
    >
      <div
        className={`ec-chat-row${channel?.isMutedByMe ? " sidebar-mute" : ""}${
          hasUnread ? " is-unread" : ""
        }`}
      >
        <div className="ec-chat-row__avatar">
          {/* Tinted tile in the channel's colour (8-digit hex: ~10% alpha) */}
          <span
            className="ec-channel-avatar"
            style={{ backgroundColor: `${accentColor}1A`, color: accentColor }}
          >
            {getInitials(channelName)}
          </span>
          {channel?.isPrivate && (
            <span className="ec-channel-avatar__lock" aria-label="Private">
              <LockFilled />
            </span>
          )}
        </div>

        <div className="ec-chat-row__body">
          <div className="ec-chat-row__line">
            <span className="ec-chat-row__name" title={channelName}>
              {channelName}
            </span>
            {!hideMoreOptions && channel?.lastMessage && (
              <span className="ec-chat-row__time">
                {formatListTime(channel.lastMessage)}
              </span>
            )}
          </div>

          <div className="ec-chat-row__line">
            <span className="ec-chat-row__subtitle">
              {notificationType || subtitle}
            </span>

            {!hideMoreOptions && (
              <div
                className="ec-chat-row__meta"
                onClick={(e) => e.stopPropagation()}
              >
                {channel.isMutedByMe && (
                  <span className="ec-chat-row__muted">
                    <MuteIcon />
                  </span>
                )}
                {unreadCount && (
                  <span className="ec-unread-badge">{unreadCount}</span>
                )}
                <Dropdown
                  menu={{ items: menuItems }}
                  className="ec-dropdown sidebar-channel-dropdown"
                  trigger={["click"]}
                  popupRender={(menu) => (
                    <div className="ec-dropdown__menu sidebar-channel-dropdown__menu">
                      {menu}
                    </div>
                  )}
                >
                  <Button
                    type="text"
                    size="small"
                    className="ec-chat-row__options"
                    aria-label="Channel options"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fa-solid fa-ellipsis" />
                  </Button>
                </Dropdown>
              </div>
            )}
          </div>
        </div>
      </div>

      {!hideMoreOptions && (
        <ConfirmationModal
          isOpen={isConfirmationPopup}
          onClose={closeConfirmationModal}
          channelId={channelId}
          onSuccess={handleConfirmationSuccess}
        />
      )}
    </div>
  );
};

export default ChannelListItem;
